import { FC, useRef, useState } from 'react'
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  Crop,
  makeAspectCrop,
  PixelCrop,
} from 'react-image-crop'
import pick from 'lodash/pick'

import { appConfig } from '@/services/config'
import { useResizeObserver } from '@/lib/hooks/useResizeObserver'

interface IEditorOrigin {
  width: number
  height: number
  naturalHeight: number
  naturalWidth: number
}

export interface IEditorCrop {
  crop: PixelCrop
  origin: IEditorOrigin
}

interface IImageEditorProps {
  image: string
  aspect?: number
  followAspect?: boolean
  onCrop: (c: IEditorCrop) => void
}

export const ImageEditor: FC<IImageEditorProps> = ({
  image,
  aspect = null,
  onCrop,
  followAspect,
}) => {
  const [scale, setScale] = useState<number>(1)
  const [prevWidth, setPrevWidth] = useState<number>(0)
  const [prevHeight, setPrevHeight] = useState<number>(0)
  const [crop, setCrop] = useState<Crop>()
  const imgElement = useRef<HTMLImageElement>()

  const centerAspectCrop = (
    mediaWidth: number,
    mediaHeight: number,
    aspect: number | null
  ) => {
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspect,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    )
  }

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (aspect) {
      const { width, height } = e.currentTarget
      setCrop(centerAspectCrop(width, height, aspect))
    }
  }

  useResizeObserver(imgElement, (entries) => {
    for (const entry of entries) {
      const width = entry.contentRect?.width
      const naturalWidth = imgElement.current?.naturalWidth

      if (width && naturalWidth && naturalWidth > 0) {
        const scale = width / naturalWidth
        setScale(scale)
        setPrevWidth(naturalWidth)
        setPrevHeight(imgElement.current?.naturalHeight)
        if (!crop) return
        onCrop({
          crop: convertToPixelCrop(
            crop,
            imgElement.current.width,
            imgElement.current.height
          ),
          origin: {
            ...pick(imgElement.current, [
              'width',
              'height',
              'naturalWidth',
              'naturalHeight',
            ]),
          },
        })
      }
    }
  })

  return (
    <ReactCrop
      className="mx-auto max-h-[90vh]"
      crop={crop}
      onChange={(intent) => {
        const { width, height, x, y } = intent
        if (height === 0) return
        const { min: lowestAspect, max: highestAspect } =
          appConfig.aspectRatioLimit

        const ratio = width / height
        const crop = { ...intent }

        const invalidRatio = ratio < lowestAspect || ratio > highestAspect
        const widthChangedMore = prevWidth - width > prevHeight - height

        if (invalidRatio) {
          if (widthChangedMore) {
            crop.height = crop.width * highestAspect
          } else {
            crop.width = crop.height * highestAspect
          }
        }

        if (
          x + crop.width > imgElement.current.width ||
          y + crop.height > imgElement.current.height
        ) {
          return
        }

        setPrevWidth(crop.width)
        setPrevHeight(crop.height)

        setCrop(crop)
      }}
      onComplete={(crop) => {
        if (imgElement.current) {
          const { width, height, naturalWidth, naturalHeight } =
            imgElement.current
          onCrop({
            crop,
            origin: { width, height, naturalHeight, naturalWidth },
          })
        }
      }}
      aspect={followAspect ? aspect : undefined}
      minWidth={scale * 200}
      minHeight={scale * 200}
      keepSelection={true}
    >
      <img
        ref={imgElement}
        src={image}
        onLoad={onImageLoad}
        className="max-h-[90vh]"
      />
    </ReactCrop>
  )
}
